/**
 * NotificationsPage — full-page Ansicht aller Watch-Notifications (nicht nur unread).
 * iter121: Route `/notifications` mapped auf `/api/me/watch-notifications`.
 * Verhält sich wie das Header-Dropdown, aber als volle Seite mit Pagination.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Loader2, TrendingDown, TrendingUp, RefreshCw, Package, ShoppingCart, Edit3, PackageX, Sparkles, MessageSquareText, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_ICON = {
  WATCH_PRICE_DROP: TrendingDown,
  WATCH_PRICE_INCREASE: TrendingUp,
  WATCH_STATUS_CHANGE: RefreshCw,
  WATCH_BACK_IN_STOCK: Package,
  WATCH_LOW_STOCK: ShoppingCart,
  WATCH_POST_UPDATED: Edit3,
  WATCH_POST_DELETED: PackageX,
  MP_NEGOTIATION_NEW: MessageSquareText,
  MP_NEGOTIATION_COUNTER: MessageSquareText,
  MP_NEGOTIATION_ACCEPTED: Sparkles,
  MP_NEGOTIATION_REJECTED: PackageX,
  NEW_ORDER: ShoppingCart,
  ORDER_RECEIVED: Package,
  ORDER_PAID: ShoppingCart,
  ORDER_SHIPPED: Package,
  ORDER_CONTENT_READY: Sparkles,
  ORDER_CONTENT_UPLOAD_REQUIRED: Edit3,
  ORDER_CONTENT_EXPIRY_SOON: RefreshCw,
  WORN_VIDEO_READY: Sparkles,
  WORN_VIDEO_UPLOAD_REQUIRED: Edit3,
  STX_DIGITAL_AVAILABLE: Sparkles,
};

function relTime(iso, t) {
  if (!iso) return "";
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return t("notifications.relative_now", { defaultValue: "gerade" });
  if (diffSec < 3600) return t("notifications.relative_min", { n: Math.floor(diffSec / 60), defaultValue: `vor ${Math.floor(diffSec / 60)} Min` });
  if (diffSec < 86400) return t("notifications.relative_hour", { n: Math.floor(diffSec / 3600), defaultValue: `vor ${Math.floor(diffSec / 3600)} Std` });
  const days = Math.floor(diffSec / 86400);
  return t("notifications.relative_day_other", { n: days, defaultValue: `vor ${days} Tagen` });
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/watch-notifications?limit=50");
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  const markAllRead = async () => {
    try {
      await api.post("/me/watch-notifications/mark-all-read");
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch { /* ignore */ }
  };

  const openItem = async (item) => {
    if (!item.read) {
      try {
        await api.post(`/me/watch-notifications/mark-read/${item.target_id}`);
        window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
      } catch { /* ignore */ }
    }
    navigate(item.link_path || "/watchlist");
  };

  return (
    <div data-testid="notifications-page" className="w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <div className="flex items-center justify-between px-4 mb-4">
        <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
          <Bell size={22} /> {t("notifications.title", { defaultValue: "Benachrichtigungen" })}
        </h1>
        {items.some((i) => !i.read) && (
          <button
            data-testid="notif-mark-all-read"
            onClick={markAllRead}
            className="text-sm text-[#666] hover:text-[#0A0A0A] font-medium flex items-center gap-1.5"
          >
            <Check size={14} /> {t("notifications.mark_all_read", { defaultValue: "Alle als gelesen markieren" })}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : items.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <Bell size={40} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">{t("notifications.empty", { defaultValue: "Keine Benachrichtigungen." })}</p>
        </div>
      ) : (
        <ul className="divide-y divide-[#F0F0F0] border border-[#E5E5E5] rounded-2xl bg-white overflow-hidden">
          {items.map((it) => {
            const Icon = TYPE_ICON[it.type] || Sparkles;
            return (
              <li key={it.notification_id}>
                <button
                  data-testid={`notification-item-${it.notification_id}`}
                  onClick={() => openItem(it)}
                  className={`w-full flex gap-3 px-4 py-3 text-left hover:bg-[#FAFAFA] transition-colors ${it.read ? "" : "bg-[#FFFBEA]"}`}
                >
                  <div className="w-11 h-14 bg-[#F5F5F5] shrink-0 overflow-hidden rounded">
                    {it.target_thumbnail_url ? (
                      <img src={it.target_thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Icon size={18} className="text-[#999]" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className="text-[#666] shrink-0" />
                      <p className="text-sm font-semibold text-[#0A0A0A] truncate">{it.title || it.type}</p>
                      {!it.read && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                    </div>
                    <p className="text-sm text-[#333] mt-0.5 line-clamp-2">{it.message}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-[#999] truncate max-w-[65%]">{it.target_title || ""}</span>
                      <span className="text-xs text-[#999]">{relTime(it.created_at, t)}</span>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
